import * as i0 from '@angular/core';
import { Component, NgModule, Pipe, inject, TemplateRef, ViewContainerRef, Directive, Input, InjectionToken, Injectable } from '@angular/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { format } from 'date-fns';
import { InteractivityChecker } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';

class A11ySrOnlyComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: A11ySrOnlyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.4", type: A11ySrOnlyComponent, isStandalone: true, selector: "vo-a11y-sr-only", ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, styles: [":host{clip:rect(1px,1px,1px,1px);clip-path:inset(50%);height:1px;width:1px;margin:-1px;overflow:hidden;padding:0;position:absolute}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: A11ySrOnlyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vo-a11y-sr-only', template: `<ng-content></ng-content>`, standalone: true, styles: [":host{clip:rect(1px,1px,1px,1px);clip-path:inset(50%);height:1px;width:1px;margin:-1px;overflow:hidden;padding:0;position:absolute}\n"] }]
        }] });

class A11yUtilsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: A11yUtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: A11yUtilsModule, imports: [A11ySrOnlyComponent], exports: [A11ySrOnlyComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: A11yUtilsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: A11yUtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [A11ySrOnlyComponent],
                    exports: [A11ySrOnlyComponent],
                }]
        }] });

class Base10Pipe {
    // only returns to 2 places, can be extended in future
    transform(values, place) {
        switch (place) {
            case 2:
                return Math.floor(values / 10);
            default:
                return Math.floor(values % 10);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: Base10Pipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: Base10Pipe, isStandalone: true, name: "base10" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: Base10Pipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'base10',
                    standalone: true,
                }]
        }] });

class Base10Module {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: Base10Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: Base10Module, imports: [Base10Pipe], exports: [Base10Pipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: Base10Module }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: Base10Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [Base10Pipe],
                    exports: [Base10Pipe],
                }]
        }] });

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

class ArrayToStringPipe {
    /**
     * Converts an array of numbers or strings, into a string
     * @param values
     * @param delimiter
     */
    transform(values, delimiter = ' ') {
        if (values === null) {
            return null;
        }
        return values.filter((value) => value !== null).join(delimiter);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ArrayToStringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: ArrayToStringPipe, isStandalone: true, name: "arrayToString" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ArrayToStringPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'arrayToString',
                    standalone: true,
                }]
        }] });

/**
 * Exposes the @angular/cdk coerceBooleanProperty function
 * as a pipe
 */
class CoerceBooleanPropertyPipe {
    transform(...values) {
        return values.map((value) => coerceBooleanProperty(value)).includes(true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoerceBooleanPropertyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: CoerceBooleanPropertyPipe, isStandalone: true, name: "coerceBooleanProperty" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoerceBooleanPropertyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'coerceBooleanProperty',
                    standalone: true,
                }]
        }] });

/**
 * Exposes the @angular/cdk coerceNumberProperty function
 * as a pipe
 */
class CoerceNumberPropertyPipe {
    transform(value) {
        return coerceNumberProperty(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoerceNumberPropertyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: CoerceNumberPropertyPipe, isStandalone: true, name: "coerceNumberProperty" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoerceNumberPropertyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'coerceNumberProperty',
                    standalone: true,
                }]
        }] });

const truthyOrNullPipe = (...values) => {
    return values.map((value) => value !== 'false' && Boolean(value)).includes(true) || null;
};
class TruthyOrNullPipe {
    constructor() {
        this.transform = truthyOrNullPipe;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: TruthyOrNullPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: TruthyOrNullPipe, isStandalone: true, name: "truthyOrNull" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: TruthyOrNullPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'truthyOrNull',
                    standalone: true,
                }]
        }] });

const valueOrNullPipe = (value) => {
    return value ? value : null;
};
/**
 * Coerces negative values to null
 */
class ValueOrNullPipe {
    constructor() {
        this.transform = valueOrNullPipe;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ValueOrNullPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: ValueOrNullPipe, isStandalone: true, name: "valueOrNull" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ValueOrNullPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'valueOrNull',
                    standalone: true,
                }]
        }] });

class CoersionModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoersionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: CoersionModule, imports: [TruthyOrNullPipe,
            ArrayToStringPipe,
            CoerceBooleanPropertyPipe,
            CoerceNumberPropertyPipe,
            ValueOrNullPipe], exports: [TruthyOrNullPipe,
            ArrayToStringPipe,
            CoerceBooleanPropertyPipe,
            CoerceNumberPropertyPipe,
            ValueOrNullPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoersionModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: CoersionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        TruthyOrNullPipe,
                        ArrayToStringPipe,
                        CoerceBooleanPropertyPipe,
                        CoerceNumberPropertyPipe,
                        ValueOrNullPipe,
                    ],
                    exports: [
                        TruthyOrNullPipe,
                        ArrayToStringPipe,
                        CoerceBooleanPropertyPipe,
                        CoerceNumberPropertyPipe,
                        ValueOrNullPipe,
                    ],
                }]
        }] });

/* istanbul ignore file */

const concatenateIdPipe = (value, ...ids) => {
    const idArray = [value, ...ids].filter(Boolean);
    return idArray.length ? idArray.join(' ') : null;
};
class ConcatenateIdPipe {
    constructor() {
        this.transform = concatenateIdPipe;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdPipe, isStandalone: true, name: "concatenateId" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'concatenateId',
                    standalone: true,
                }]
        }] });

class ConcatenateIdModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdModule, imports: [ConcatenateIdPipe], exports: [ConcatenateIdPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: ConcatenateIdModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ConcatenateIdPipe],
                    exports: [ConcatenateIdPipe],
                }]
        }] });

class DateFnsFormatPipe {
    constructor() {
        this.transform = format;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DateFnsFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: DateFnsFormatPipe, isStandalone: true, name: "dateFnsFormat" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DateFnsFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dateFnsFormat',
                    standalone: true,
                }]
        }] });

class DateFnsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DateFnsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: DateFnsModule, imports: [DateFnsFormatPipe], exports: [DateFnsFormatPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DateFnsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DateFnsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DateFnsFormatPipe],
                    exports: [DateFnsFormatPipe],
                }]
        }] });

class DuplicateByDirective {
    constructor() {
        this.templateRef = inject((TemplateRef));
        this.viewContainerRef = inject(ViewContainerRef);
    }
    set count(value) {
        this.viewContainerRef.clear();
        for (let i = 1; i < value; i++) {
            const context = { count: i };
            this.viewContainerRef.createEmbeddedView(this.templateRef, context);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DuplicateByDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.4", type: DuplicateByDirective, isStandalone: true, selector: "[voDuplicateBy]", inputs: { count: ["voDuplicateBy", "count"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DuplicateByDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voDuplicateBy]',
                    standalone: true,
                }]
        }], propDecorators: { count: [{
                type: Input,
                args: ['voDuplicateBy']
            }] } });

class DuplicateByModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DuplicateByModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: DuplicateByModule, imports: [DuplicateByDirective], exports: [DuplicateByDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DuplicateByModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: DuplicateByModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DuplicateByDirective],
                    exports: [DuplicateByDirective],
                }]
        }] });

const INTERACTIVITY_CHECKER_CONFIG = new InjectionToken('InteractivityCheckerConfig');
const InteractivityCheckerProvider = {
    provide: INTERACTIVITY_CHECKER_CONFIG,
    useValue: {
        ignoreVisibility: false,
    },
};

class InteractivityCheckerPipe {
    #checker = inject(InteractivityChecker);
    #config = inject(INTERACTIVITY_CHECKER_CONFIG);
    #getFirstTabbableElement(root) {
        if (this.#checker.isFocusable(root, this.#config) && this.#checker.isTabbable(root)) {
            return root;
        }
        const children = root.children;
        for (let i = 0; i < children.length; i++) {
            const tabbableChild = this.#getFirstTabbableElement(children[i]);
            if (tabbableChild) {
                return tabbableChild;
            }
        }
        return null;
    }
    transform(root) {
        return !!this.#getFirstTabbableElement(root);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerPipe, isStandalone: true, name: "interactivityChecker", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'interactivityChecker',
                    pure: false,
                    standalone: true,
                }]
        }] });

class InteractivityCheckerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerModule, imports: [InteractivityCheckerPipe], exports: [InteractivityCheckerPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerModule, providers: [InteractivityCheckerProvider] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: InteractivityCheckerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [InteractivityCheckerPipe],
                    providers: [InteractivityCheckerProvider],
                    exports: [InteractivityCheckerPipe],
                }]
        }] });

const defaultOptions = {
    root: null,
    rootMargin: '300px',
    threshold: 0.05,
};
class IntersectionService {
    constructor() {
        this.document = inject(DOCUMENT);
        this.window = this.document.defaultView;
    }
    /** Checks to see if Intersection Observer is supported within the current environment */
    isSupported() {
        return !!this.window && 'IntersectionObserver' in this.window;
    }
    /** Create a new Intersection Observer */
    create(callback, opts = defaultOptions) {
        if (this.window && this.isSupported()) {
            return new this.window.IntersectionObserver(callback, opts);
        }
        throw new Error('IntersectionObserver is not supported in this browser');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IntersectionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IntersectionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IntersectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

var Key;
(function (Key) {
    Key["ArrowDown"] = "ArrowDown";
    Key["ArrowUp"] = "ArrowUp";
    Key["ArrowRight"] = "ArrowRight";
    Key["ArrowLeft"] = "ArrowLeft";
    Key["Left"] = "Left";
    Key["Right"] = "Right";
    Key["PageDown"] = "PageDown";
    Key["PageUp"] = "PageUp";
    Key["Home"] = "Home";
    Key["End"] = "End";
    Key["Space"] = " ";
    Key["Enter"] = "Enter";
    Key["Escape"] = "Escape";
    Key["Backspace"] = "Backspace";
    Key["Delete"] = "Delete";
    Key["Tab"] = "Tab";
    Key["Shift"] = "Shift";
    Key["Control"] = "Control";
    Key["Alt"] = "Alt";
    Key["Number0"] = "0";
    Key["Number1"] = "1";
    Key["Number2"] = "2";
    Key["Number3"] = "3";
    Key["Number4"] = "4";
    Key["Number5"] = "5";
    Key["Number6"] = "6";
    Key["Number7"] = "7";
    Key["Number8"] = "8";
    Key["Number9"] = "9";
})(Key || (Key = {}));

const idCounter = new Map();
/**
 * @returns a unique identifier starting with `prefix`
 * Used to generate unique ids for components and other DOM elements
 *
 * @param prefix - the prefix to use for the unique id
 */
function generateUniqueId(prefix) {
    const prev = idCounter.get(prefix);
    const next = prev !== undefined ? prev + 1 : 0;
    idCounter.set(prefix, next);
    return `${prefix}-${next}`;
}

const idSuffixPipe = (id, ...suffix) => id ? [id, ...suffix].join('-') : null;
class IdSuffixPipe {
    constructor() {
        this.transform = idSuffixPipe;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IdSuffixPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: IdSuffixPipe, isStandalone: true, name: "idSuffix" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IdSuffixPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'idSuffix',
                    standalone: true,
                }]
        }] });

class UniqueIdPipe {
    transform(id, prefix = 'unique') {
        return id || generateUniqueId(prefix);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdPipe, isStandalone: true, name: "uniqueId" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'uniqueId',
                    standalone: true,
                }]
        }] });

class UniqueIdModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdModule, imports: [UniqueIdPipe, IdSuffixPipe], exports: [UniqueIdPipe, IdSuffixPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: UniqueIdModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [UniqueIdPipe, IdSuffixPipe],
                    exports: [UniqueIdPipe, IdSuffixPipe],
                }]
        }] });

function idAttribute(value) {
    return value ?? generateUniqueId('id');
}

/**
 * Generated bundle index. Do not edit.
 */

export { A11ySrOnlyComponent, A11yUtilsModule, ArrayToStringPipe, Base10Module, Base10Pipe, CoerceBooleanPropertyPipe, CoerceNumberPropertyPipe, CoersionModule, ConcatenateIdModule, ConcatenateIdPipe, DateFnsFormatPipe, DateFnsModule, DuplicateByDirective, DuplicateByModule, INTERACTIVITY_CHECKER_CONFIG, IdSuffixPipe, InteractivityCheckerModule, InteractivityCheckerPipe, InteractivityCheckerProvider, IntersectionService, Key, TruthyOrNullPipe, UniqueIdModule, UniqueIdPipe, ValueOrNullPipe, clamp, concatenateIdPipe, generateUniqueId, idAttribute, idSuffixPipe, truthyOrNullPipe, valueOrNullPipe };
